import React, { createContext, useContext, useEffect, useState } from 'react';
import { SettingsSchema, SectionConfig, SectionItemConfig } from './types';
import { getSettings, loadClientStylesheet, getAppHostname } from './api/assets';

const defaultSettings: SettingsSchema = {
  pageTitle: 'White Label',
  favIcon: '',
  isSurfMVNE: false,
  externalPages: [],
  socials: {},
  appLinks: {},
  topBar: {},
  middlewareUrl: '',
  hideApp: false,
  salesUrl: '',
  companyAddress: '',
  companyName: '',
  companyCNPJ: '',
  pages: [],
};

/**
 * Deep merge sections by "name" key.
 * Client settings override defaults at the item level using "name" as merge key.
 */
function mergeSectionItems(
  defaultItems: SectionItemConfig[],
  overrideItems: SectionItemConfig[]
): SectionItemConfig[] {
  const merged = [...defaultItems];

  for (const override of overrideItems) {
    const existingIndex = merged.findIndex((item) => item.name === override.name);
    if (existingIndex >= 0) {
      merged[existingIndex] = { ...merged[existingIndex], ...override };
    } else {
      merged.push(override);
    }
  }

  return merged;
}

function mergeSections(
  defaultSections: SectionConfig[],
  overrideSections: SectionConfig[]
): SectionConfig[] {
  const merged = [...defaultSections];

  for (const override of overrideSections) {
    const existingIndex = merged.findIndex((s) => s.name === override.name);
    if (existingIndex >= 0) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...override,
        items: mergeSectionItems(
          merged[existingIndex].items || [],
          override.items || []
        ),
      };
    } else {
      merged.push(override);
    }
  }

  return merged;
}

export function mergeSettings(
  defaults: SettingsSchema,
  overrides: Partial<SettingsSchema>
): SettingsSchema {
  const merged = { ...defaults, ...overrides };

  // Deep merge pages by route
  if (defaults.pages && overrides.pages) {
    const mergedPages = [...defaults.pages];
    for (const overridePage of overrides.pages) {
      const existingIndex = mergedPages.findIndex(
        (p) => p.route === overridePage.route
      );
      if (existingIndex >= 0) {
        mergedPages[existingIndex] = {
          ...mergedPages[existingIndex],
          ...overridePage,
          sections: mergeSections(
            mergedPages[existingIndex].sections || [],
            overridePage.sections || []
          ),
        };
      } else {
        mergedPages.push(overridePage);
      }
    }
    merged.pages = mergedPages;
  }

  return merged;
}

interface SettingsContextValue {
  settings: SettingsSchema;
  loading: boolean;
  error: string | null;
  app: string;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  loading: true,
  error: null,
  app: '',
});

export function useSettings(): SettingsContextValue {
  return useContext(SettingsContext);
}

interface SettingsProviderProps {
  children: React.ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<SettingsSchema>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const app = getAppHostname();

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // 1. Load settings
        const clientSettings = await getSettings(app);
        const merged = mergeSettings(defaultSettings, clientSettings);

        if (cancelled) return;

        // 2. Apply page title
        document.title = merged.pageTitle;

        // 3. Apply favicon
        if (merged.favIcon) {
          let favicon = document.querySelector<HTMLLinkElement>(
            'link[rel="icon"]'
          );
          if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
          }
          favicon.href = merged.favIcon;
        }

        // 4. Load client stylesheet
        await loadClientStylesheet(app);

        if (cancelled) return;

        setSettings(merged);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load settings:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load settings'
        );
        setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [app]);

  return (
    <SettingsContext.Provider value={{ settings, loading, error, app }}>
      {children}
    </SettingsContext.Provider>
  );
}
